<template>
  <div
    class="bootcamp-overview-card"
    :style="`background-image: url(${bootcampBackgroundImage});`"
  >
    <div class="bootcamp-overview-card__mask"></div>
    <div class="bootcamp-overview-card__header">
      <div class="bootcamp-overview-card__pass">
        <Passes :passName="learningAreaName" :size="45" />
      </div>
      <h4 class="bootcamp-overview-card__title">{{ bootcamp.name }}</h4>
    </div>
    <div class="bootcamp-overview-card__footer">
      <div class="bootcamp-overview-card__description-wrapper">
        <p
          class="bootcamp-overview-card__description"
          v-html="renderMarkdown(bootcamp.description)"
        ></p>
        <div v-if="isCtaHidden" class="bootcamp-overview-card__status-label">
          This event has finished
        </div>
      </div>
      <div class="bootcamp-overview-card__action">
        <p v-if="!hasAccess" class="bootcamp-overview-card__premium-cta">
          <Passes
            passName="Premium"
            :size="50"
            class="bootcamp-overview-card__premium-cta--icon"
          />
          Premium Plan Required
        </p>
        <template v-if="!hasAccess">
          <button
            v-if="canUpgradePlan"
            class="btn btn--secondary"
            @click="handleClick"
          >
            <LockIcon />
            UPGRADE
          </button>
          <a
            v-else
            class="btn btn--secondary"
            :href="`//${plansLink}`"
            id="ine-link"
          >
            <LockIcon />
            Get access
          </a>
        </template>
        <Action
          v-if="hasAccess && isBootcampRegisterable"
          @click="handleClick"
          ofType="large"
          :content="buttonText"
          :customClass="
            userHasRegistered
              ? 'bootcamp-overview-card__register-btn bootcamp-overview-card__register-btn--disabled'
              : 'bootcamp-overview-card__register-btn'
          "
        />
        <Action
          v-if="hasAccess && !isBootcampRegisterable && !isCtaHidden"
          ofType="large"
          :content="registrationStatus"
          :disabled="isCtaDisabled"
          @click="handleFinishedCtaClick()"
          :customClass="
            userHasRegistered
              ? 'bootcamp-overview-card__register-btn bootcamp-overview-card__register-btn--disabled'
              : 'bootcamp-overview-card__register-btn bootcamp-overview-card__register-btn--information'
          "
        />
      </div>
      <div
        class="bootcamp-overview-card__coming-soon-info"
        v-if="contentComingSoon"
      >
        The on-demand videos will be available soon
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Passes from '@/components/properties/Passes.vue'
import Action from '@/components/shared/Action.vue'
import LockIcon from '@/assets/icons/fill/lock.svg'
import CONSTANTS from '@/constants'

export default {
  name: 'BootcampOverviewCard',
  components: {
    Passes,
    Action,
    LockIcon: { ...LockIcon, name: 'LockIcon' },
  },
  props: {
    bootcamp: {
      type: Object,
      required: true,
    },
    learningAreaName: {
      type: String,
      required: true,
    },
    renderMarkdown: {
      type: Function,
      required: true,
    },
    hasAccess: {
      type: Boolean,
      required: true,
    },
    userHasRegistered: {
      type: Boolean,
      required: true,
    },
    isBootcampRegisterable: {
      type: Boolean,
      required: true,
    },
    canUpgradePlan: {
      type: Boolean,
      require: false,
    },
  },
  computed: {
    ...mapState({
      plansLink: state => state.app.plansLink,
    }),
    contentComingSoon() {
      return (
        this.bootcamp.status ===
        CONSTANTS.BOOTCAMP_CONTENT_STATUS.CONTENT_COMING_SOON
      )
    },
    buttonText() {
      if (!this.hasAccess) {
        return 'Upgrade'
      }
      return this.userHasRegistered ? 'Registered' : 'Register'
    },
    registrationStatus() {
      let registration_status = this.bootcamp.registration_status.replace(
        /_/g,
        ' '
      )
      registration_status =
        registration_status.charAt(0).toUpperCase() +
        registration_status.slice(1)
      return this.bootcamp.registration_status ===
        CONSTANTS.BOOTCAMP_REGISTRATION_STATUS.FINISHED &&
        this.bootcamp.status === CONSTANTS.BOOTCAMP_CONTENT_STATUS.PUBLISHED &&
        this.bootcamp.related_courses?.length
        ? 'Watch recordings'
        : registration_status
    },
    isCtaDisabled() {
      return (
        this.bootcamp.registration_status !==
          CONSTANTS.BOOTCAMP_REGISTRATION_STATUS.FINISHED ||
        (this.bootcamp.registration_status ===
          CONSTANTS.BOOTCAMP_REGISTRATION_STATUS.FINISHED &&
          !this.bootcamp.related_courses?.length)
      )
    },
    isCtaHidden() {
      return (
        this.bootcamp.registration_status ===
          CONSTANTS.BOOTCAMP_REGISTRATION_STATUS.FINISHED &&
        (this.bootcamp.status === CONSTANTS.BOOTCAMP_CONTENT_STATUS.PUBLISHED ||
          this.bootcamp.status ===
            CONSTANTS.BOOTCAMP_CONTENT_STATUS.CONTENT_PUBLISHED) &&
        !this.bootcamp.related_courses?.length
      )
    },
    bootcampBackgroundImage() {
      return (
        this.bootcamp.featured_background_image_url ||
        require('@/assets/images/bootcamps/bootcamp_bg_big.png')
      )
    },
  },
  methods: {
    handleClick() {
      if (!this.hasAccess) {
        this.$emit('handle-upgrade-plan-to-premium')
      } else {
        this.$emit('handle-bootcamp-registration')
      }
    },
    handleFinishedCtaClick() {
      this.$emit('handle-bootcamp-finished-scroll')
    },
  },
}
</script>

<style lang="scss">
.bootcamp-overview-card {
  border-radius: 10px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: left;
  position: relative;
  width: 100%;
  color: var(--white);
  @include tablet {
    background-position: center;
  }
  @include desktop {
    display: flex;
    flex-wrap: wrap;
    padding: 50px 40px;
    min-height: 385px;
  }
  &__mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0.3),
      rgba(0, 0, 0, 0.4),
      rgba(0, 0, 0, 0.5)
    );
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    display: none;
    z-index: 0;
    @include desktop {
      display: block;
    }
  }
  &__header {
    padding: 40px;
    z-index: 1;
    @include desktop {
      display: flex;
      padding: 0;
      width: 100%;
    }
  }
  &__title {
    font-family: var(--font-bold);
    max-width: 233px;
    padding: 0;
    margin: 15px 0 0 0;
    @include desktop {
      margin: 12px 0 0 30px;
      width: 80%;
      max-width: 50%;
    }
  }
  &__coming-soon-info {
    margin-top: 10px;
    text-align: right;
    font-style: italic;
    width: 100%;
  }
  &__footer {
    padding: 40px;
    background: rgba(0, 0, 0, 0.5);
    border-bottom-right-radius: 10px;
    border-bottom-left-radius: 10px;
    z-index: 1;
    @include desktop {
      padding: 45px 0 0 0;
      background: none;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
    }
  }
  &__description-wrapper {
    display: flex;
    flex-direction: column;
  }
  &__description {
    font-size: rem(16px);
    width: 100%;
    margin: 0 0 15px 0;
    @include desktop {
      padding: 0 10px 0 80px;
      max-width: calc(50% + 80px);
    }
  }
  &__status-label {
    font-size: rem(12);
    line-height: 20px;
    @include desktop {
      padding-left: 80px;
    }
  }
  &__action {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin: 0 0 0 auto;
    width: 100%;
  }
  &__premium-cta {
    max-width: 180px;
    display: flex;
    align-items: center;
    &--icon {
      margin: 0 5px 0 0;
    }
  }
}
</style>
